import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../services/api';
import { toast } from 'react-toastify';

const AuthContext = createContext(null);

const INACTIVITY_TIMEOUT = 14 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [inactivityModalOpen, setInactivityModalOpen] = useState(false);
  // Use ref for timeout to avoid stale closures in event listeners
  const timeoutRef = useRef(null);

  const resetInactivityTimer = useCallback(() => {
    if (!isAuthenticated) return;

    // console.log('Activity detected, resetting timer'); // Uncomment for verbose debugging

    localStorage.setItem('lastActivity', Date.now().toString());
    
    // If they were locked, we don't automatically unlock on simple activity 
    // but the modal should be handled by verify password.
    // However, if we just want to ensure that if they refresh they are redirected, 
    // we set the flag when timeout happens.

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      console.log('Inactivity timeout reached, showing password prompt...');
      localStorage.setItem('isLocked', 'true');
      setInactivityModalOpen(true);
    }, INACTIVITY_TIMEOUT);
  }, [isAuthenticated]);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timeout if user logs out
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Define listener specific to this effect cycle
    const handleActivity = () => resetInactivityTimer();

    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Initialize timer
    resetInactivityTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated, resetInactivityTimer]);

  const checkAuth = async () => {
    // Don't check auth if already on login page
    if (window.location.pathname === '/login') {
      setLoading(false);
      return;
    }

    // Check if system was locked before refresh
    if (localStorage.getItem('isLocked') === 'true') {
      console.log('System was locked due to inactivity. Redirecting to login.');
      await logout();
      setLoading(false);
      return;
    }

    // Check last activity time
    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity && Date.now() - parseInt(lastActivity) > INACTIVITY_TIMEOUT) {
      console.log('Inactivity period exceeded. Redirecting to login.');
      await logout();
      setLoading(false);
      return;
    }

    try {
      console.log('Checking admin authentication session...');
      const response = await authAPI.getProfile();
      console.log('Admin auth check success');
      setAdmin(response.data.data.admin);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);

      if (response.data.message === '2FA_REQUIRED') {
        return { success: true, twoFactorRequired: true, adminId: response.data.data.adminId };
      }

      const { admin } = response.data.data;

      setAdmin(admin);
      setIsAuthenticated(true);
      localStorage.removeItem('isLocked');
      localStorage.setItem('lastActivity', Date.now().toString());

      toast.success(`Welcome back, ${admin.fullName}!`);
      return { success: true };
    } catch (error) {
      let message = 'Login failed';

      if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.response?.status === 401) {
        message = 'Invalid email or password';
      } else if (error.response?.status === 403) {
        message = 'Your account has been deactivated. Contact super admin.';
      } else if (error.message) {
        message = error.message;
      }

      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verify2FA = async (data) => {
    try {
      const response = await authAPI.verify2FA(data);
      const { admin } = response.data.data;

      setAdmin(admin);
      setIsAuthenticated(true);
      localStorage.removeItem('isLocked');
      localStorage.setItem('lastActivity', Date.now().toString());

      toast.success(`Welcome back, ${admin.fullName}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || '2FA verification failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAdmin(null);
      setIsAuthenticated(false);
      localStorage.removeItem('isLocked');
      localStorage.removeItem('lastActivity');
      toast.info('Logged out successfully');
    }
  };

  const changePassword = async (passwordData) => {
    try {
      await authAPI.changePassword(passwordData);
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to change password';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const handleInactivityPasswordVerified = () => {
    localStorage.removeItem('isLocked');
    setInactivityModalOpen(false);
    resetInactivityTimer();
  };

  const handleInactivityModalClose = async () => {
    setInactivityModalOpen(false);
    await logout();
  };

  const value = {
    admin,
    loading,
    isAuthenticated,
    login,
    verify2FA,
    logout,
    changePassword,
    checkAuth,
    inactivityModalOpen,
    handleInactivityPasswordVerified,
    handleInactivityModalClose,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;